<script setup lang="ts">
import { computed, onMounted, useTemplateRef, watch } from 'vue';

const model = defineModel({
    type: Boolean,
    default: false
})

const {
    closeOutside = false,
    title = '',
    variant = 'normal',
    dismissible = false,
    actionLabel = '',
    width = 'w-110',
    icon = true,
} = defineProps<{
    closeOutside?: boolean
    // Giving a title turns the dialog into a standard popup: icon + title header, left-aligned
    // body, optional full-width action button. Without one it stays a bare dialog the caller
    // styles and fills itself (the mobile side menu).
    title?: string
    // 'error' paints the icon, the title and the border red; 'normal' keeps them black.
    variant?: 'normal' | 'error'
    // The info icon only makes sense on popups that interrupt: panels the participant opened
    // on purpose (support, instructions) show the title alone.
    icon?: boolean
    // Adds an X to the header, for popups that have no closing button of their own.
    dismissible?: boolean
    // When set, the popup ends with a full-width button carrying this label.
    actionLabel?: string
    // Tailwind width utility for the panel, for the rare popup that needs to be wider.
    width?: string
}>()

const events = defineEmits([
    'show',
    'close',
    'action'
])

const isError = computed(() => variant === 'error')

function act() {
    events('action')
    model.value = false
}

const modal = useTemplateRef("modal")

watch(model, () => {
    // Drive the native <dialog> in both directions: opening AND closing. Previously only the
    // open branch called toggle(), so setting the model back to false never closed the dialog.
    toggle(model.value)
})

function toggle(on) {
    const dlg = modal.value
    // Guard on THIS dialog's own open state, not on whether *any* modal is open — otherwise a
    // modal opened on top of another (e.g. the instructions modal over the mobile side menu)
    // would be refused because a dialog is already showing.
    if (!dlg || on === dlg.open) {
        return
    }

    events(on ? 'show' : 'close')

    if (on) {
        dlg.showModal()
    }
    else {
        dlg.close()
    }
}

function outsideClick() {
    if (!closeOutside) {
        return
    }
    
    model.value = false
}

onMounted(() => {
    if (model.value) {
        toggle(true)
    }
})

defineExpose({
    toggle
})

</script>

<template>
    <dialog ref="modal" class="overflow-visible max-h-none max-w-none" @click.self="outsideClick()"
        :class="title ? ['m-auto rounded-lg border-[3px]', isError ? 'border-error bg-error-100' : 'border-gray-900 bg-white'] : ''">
        <slot v-if="!title"></slot>
        <div v-else class="col gap-3 p-5 max-w-[92vw] max-h-[85vh] text-left" :class="width">
            <div class="row items-center gap-2 shrink-0">
                <img v-if="icon" :src="isError ? '/assets/info-red.svg' : '/assets/info-black.svg'"
                    alt="" class="size-5 shrink-0" />
                <span class="flex-1 text-md font-bold" :class="isError ? 'text-error' : 'text-black'">
                    {{ title }}
                </span>
                <button v-if="dismissible" @click="model = false"
                    class="btn-primary flex items-center justify-center size-8 shrink-0">
                    X
                </button>
            </div>
            <div class="flex-1 min-h-0 overflow-y-auto text-sm">
                <slot></slot>
            </div>
            <button v-if="actionLabel" @click="act" class="btn-primary w-full py-2.5! shrink-0">
                {{ actionLabel }}
            </button>
        </div>
    </dialog>
</template>

<style scoped>
@reference "tailwindcss";
@reference "#app.css";

@keyframes bg {
    from { opacity: 0; };
}

dialog::backdrop {
    @apply bg-gray-800 opacity-75;

    animation-name: bg;
    animation-duration: var(--default-anim-duration);
    animation-timing-function: var(--default-anim-timing);
}
</style>